import React, { useState, useMemo } from "react";
import PageLoader from "../components/ui/PageLoader";
import Modal from "../components/ui/Modal";
import Button from "../components/ui/Button";
import {
  useGetCompanyOrdersAdmin,
  useProcessRefund,
  useRejectRefund,
} from "apis/companyOrder";
import Table from "components/ui/Table";

const CompanyOrder = () => {
  const [viewModal, setViewModal] = useState(false);
  const [refundModal, setRefundModal] = useState(false);
  const [rejectModal, setRejectModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [refundAmount, setRefundAmount] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const pageSize = 10;

  const { data: ordersData, isPending } = useGetCompanyOrdersAdmin();
  const orders = ordersData?.data || [];

  const processRefund = useProcessRefund();
  const rejectRefund = useRejectRefund();

  const formatCurrency = (amount) =>
    `$${(amount || 0).toLocaleString("en-AU", { minimumFractionDigits: 2 })}`;

  const formatDate = (date) =>
    date
      ? new Date(date).toLocaleDateString("en-AU", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "-";

  const getStatusBadge = (status) => {
    const styles = {
      paid: "bg-green-100 text-green-800",
      refundRequested: "bg-orange-100 text-orange-800",
      refunded: "bg-blue-100 text-blue-800",
      failed: "bg-red-100 text-red-800",
    };
    const label = status === "refundRequested" ? "Pending Refund" : status;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${styles[status] || "bg-lightgray text-darkgray"}`}>
        {label}
      </span>
    );
  };

  const handleProcessRefund = async () => {
    if (!selectedOrder) return;
    await processRefund.mutateAsync({
      orderId: selectedOrder._id,
      refundAmount: parseFloat(refundAmount),
    });
    setRefundModal(false);
    setSelectedOrder(null);
  };

  const handleRejectRefund = async () => {
    if (!selectedOrder) return;
    await rejectRefund.mutateAsync({
      orderId: selectedOrder._id,
      rejectionReason,
    });
    setRejectModal(false);
    setSelectedOrder(null);
    setRejectionReason("");
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "index",
        header: "#",
        cell: (_, rowIndex, currentPage) =>
          (currentPage - 1) * pageSize + rowIndex + 1,
        isFilter: false,
        isSortable: false,
      },
      {
        accessorKey: "createdAt",
        header: "Date",
        cell: (row) => formatDate(row.createdAt),
        isFilter: false,
      },
      {
        accessorKey: "companyId.name",
        header: "Company",
        cell: (row) => row.companyId?.name || "-",
        isFilter: true,
      },
      {
        accessorKey: "productName",
        header: "Product Name",
        isFilter: true,
      },
      {
        accessorKey: "totalAmount",
        header: "Amount",
        cell: (row) => formatCurrency(row.totalAmount),
        isFilter: false,
      },
      {
        accessorKey: "status",
        header: "Status",
        cell: (row) => getStatusBadge(row.status),
        isFilter: false,
      },
      {
        accessorKey: "actions",
        header: "Actions",
        cell: (row) => {
          const order = row;
          return (
            <div className="flex items-center gap-2">
              {order.status === "refundRequested" && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedOrder(order);
                      setRefundAmount(order.totalAmount?.toString() || "");
                      setRefundModal(true);
                    }}
                    className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                  >
                    Approve
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedOrder(order);
                      setRejectModal(true);
                    }}
                    className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                  >
                    Reject
                  </button>
                </>
              )}

              {order.status === "paid" && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedOrder(order);
                    setRefundAmount(order.totalAmount?.toString() || "");
                    setRefundModal(true);
                  }}
                  className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  Refund
                </button>
              )}
            </div>
          );
        },
        isFilter: false,
        isSortable: false,
        stopPropagation: true,
      },
    ],
    []
  );

  if (isPending) return <PageLoader />;

  const handleViewRowClick = (row) => {
    setSelectedOrder(row);
    setViewModal(true);
  };

  return (
    <>
      {/* View Modal */}
      <Modal isOpen={viewModal} onClose={() => setViewModal(false)} title="Order Details">
        {selectedOrder && (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-darkgray">Company</p>
                <p className="font-medium">{selectedOrder.companyId?.name}</p>
              </div>
              <div>
                <p className="text-darkgray">Product Name</p>
                <p className="font-medium">{selectedOrder.productName}</p>
              </div>
              <div>
                <p className="text-darkgray">Status</p>
                {getStatusBadge(selectedOrder.status)}
              </div>
              <div>
                <p className="text-darkgray">Amount</p>
                <p className="font-semibold">{formatCurrency(selectedOrder.totalAmount)}</p>
              </div>
              <div>
                <p className="text-darkgray">Date</p>
                <p>{formatDate(selectedOrder.createdAt)}</p>
              </div>
            </div>
            {selectedOrder.refundRequest?.reason && (
              <div className="bg-orange-50 border border-orange-200 rounded p-3">
                <p className="text-xs text-darkgray">Refund Reason</p>
                <p className="text-sm">{selectedOrder.refundRequest.reason}</p>
              </div>
            )}
            {selectedOrder.receiptUrl && (
              <a href={selectedOrder.receiptUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm">
                View Receipt →
              </a>
            )}
          </div>
        )}
      </Modal>

      {/* Refund Modal */}
      <Modal isOpen={refundModal} onClose={() => setRefundModal(false)} title="Process Refund">
        {selectedOrder && (
          <div className="space-y-4">
            <div className="bg-lightgray rounded p-3">
              <p className="font-medium">{selectedOrder.productName}</p>
              <p className="text-sm text-darkgray">{selectedOrder.companyId?.name}</p>
            </div>
            {selectedOrder.refundRequest?.reason && (
              <div className="bg-orange-50 border border-orange-200 rounded p-3">
                <p className="text-xs text-darkgray">Customer's Reason</p>
                <p className="text-sm">{selectedOrder.refundRequest.reason}</p>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-1">Refund Amount</label>
              <input
                type="number"
                value={refundAmount}
                onChange={(e) => setRefundAmount(e.target.value)}
                max={selectedOrder.totalAmount}
                step="0.01"
                className="w-full border border-mediumgray rounded px-3 py-2"
              />
              <p className="text-xs text-darkgray mt-1">Max: {formatCurrency(selectedOrder.totalAmount)}</p>
            </div>
            <div className="flex gap-3">
              <Button className="flex-1 gray-button" onClick={() => setRefundModal(false)}>Cancel</Button>
              <Button className="flex-1 black-button" onClick={handleProcessRefund} loading={processRefund.isPending}>
                Process Refund
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Reject Modal */}
      <Modal isOpen={rejectModal} onClose={() => setRejectModal(false)} title="Reject Refund">
        {selectedOrder && (
          <div className="space-y-4">
            <div className="bg-lightgray rounded p-3">
              <p className="font-medium">{selectedOrder.productName}</p>
              <p className="text-sm text-darkgray">{selectedOrder.companyId?.name}</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Rejection Reason</label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                className="w-full border border-mediumgray rounded px-3 py-2 resize-none"
                rows={3}
                placeholder="Explain why..."
              />
            </div>
            <div className="flex gap-3">
              <Button className="flex-1 gray-button" onClick={() => setRejectModal(false)}>Cancel</Button>
              <Button
                className="flex-1 black-button bg-red-600 hover:bg-red-700"
                onClick={handleRejectRefund}
                loading={rejectRefund.isPending}
                disabled={!rejectionReason.trim()}
              >
                Reject
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Table */}
      <div className="container mx-auto">
        <Table
          title="Refunds"
          columns={columns}
          data={orders}
          pageSize={pageSize}
          onRowClick={handleViewRowClick}
        />
      </div>
    </>
  );
};

export default CompanyOrder;